import React from 'react';

/**
 * Memoized Actions Cell Renderer for Sales Table
 * Renders action buttons (Call, Edit, Archive/Restore) for each row
 */
const ActionsCellRenderer = React.memo((props) => {
  const {
    data: item,
    activeCallStatuses,
    entityName,
    basePath,
    customActions,
    onPhoneClick,
    onEmailClick,
    onEditClick,
    onArchiveClick,
    onRestoreClick
  } = props;

  const isArchived = item.is_active === false;
  const callInfo = activeCallStatuses[String(item.id)];

  const getPhoneButton = () => {
    const handleClick = () => onPhoneClick(item.id);

    if (!callInfo || callInfo.status === undefined) {
      return (
        <button className="btn btn-sm btn-success" onClick={handleClick} title={`Call ${entityName}`}>
          <i className="fas fa-phone"></i>
        </button>
      );
    }

    switch (callInfo.status) {
      case 'queued':
      case 'pending':
      case 'scheduled':
        return (
          <button className="btn btn-sm btn-info btn-phone-queued" onClick={handleClick} title={`Call queued (Position: ${callInfo.queue_position || '?'})`}>
            <i className="fas fa-phone me-1"></i>
            <i className="fas fa-clock fa-xs"></i>
          </button>
        );
      case 'registered':
      case 'initiated':
      case 'in_progress':
        return (
          <button className="btn btn-sm btn-warning btn-phone-initiating" onClick={handleClick} title="Initiating call...">
            <i className="fas fa-phone me-1"></i>
            <i className="fas fa-ellipsis-h fa-xs"></i>
          </button>
        );
      case 'ongoing':
      case 'call_started':
        return (
          <button className="btn btn-sm btn-phone-ongoing" onClick={handleClick} title="Call in progress">
            <i className="fas fa-phone-volume me-1"></i>
            <i className="fas fa-circle fa-xs" style={{ fontSize: '0.4rem', verticalAlign: 'middle' }}></i>
          </button>
        );
      case 'ended':
      case 'completed':
      case 'analyzed':
      case 'call_ended':
        return (
          <button className="btn btn-sm btn-success btn-phone-completed" onClick={handleClick} title="Call completed — click to call again">
            <i className="fas fa-phone me-1"></i>
            <i className="fas fa-check fa-xs"></i>
          </button>
        );
      case 'error':
      case 'failed':
      case 'canceled':
      case 'cancelled':
        return (
          <button className="btn btn-sm btn-danger btn-phone-failed" onClick={handleClick} title="Call failed — click to retry">
            <i className="fas fa-phone-slash"></i>
          </button>
        );
      default:
        return (
          <button className="btn btn-sm btn-success" onClick={handleClick} title={`Call ${entityName}`}>
            <i className="fas fa-phone"></i>
          </button>
        );
    }
  };

  return (
    <div className="d-flex align-items-center gap-1" style={{ height: '100%' }}>
      {!isArchived && getPhoneButton()}
      {!isArchived && onEmailClick && (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => onEmailClick(item.id)}
          title="Send Email"
        >
          <i className="fas fa-envelope"></i>
        </button>
      )}
      {!isArchived && (
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => {}}
          title="LinkedIn (Coming Soon)"
          disabled
          style={{ opacity: 0.5, cursor: 'not-allowed' }}
        >
          <i className="fab fa-linkedin"></i>
        </button>
      )}
      <button
        className="btn btn-sm btn-info"
        onClick={() => onEditClick(item.id)}
        title="Edit Full Form"
      >
        <i className="fas fa-edit"></i>
      </button>
      {isArchived ? (
        <button
          className="btn btn-sm btn-success"
          onClick={() => onRestoreClick(item)}
          title={`Restore ${entityName}`}
        >
          <i className="fas fa-undo"></i>
        </button>
      ) : (
        <button
          className="btn btn-sm btn-warning"
          onClick={() => onArchiveClick(item)}
          title={`Archive ${entityName}`}
        >
          <i className="fas fa-archive"></i>
        </button>
      )}
      {customActions?.renderConvertButton && customActions.renderConvertButton(item)}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for optimal re-rendering
  return (
    prevProps.data.id === nextProps.data.id &&
    prevProps.data.is_active === nextProps.data.is_active &&
    prevProps.activeCallStatuses[String(prevProps.data.id)]?.status ===
      nextProps.activeCallStatuses[String(nextProps.data.id)]?.status &&
    prevProps.activeCallStatuses[String(prevProps.data.id)]?.queue_position ===
      nextProps.activeCallStatuses[String(nextProps.data.id)]?.queue_position
  );
});

ActionsCellRenderer.displayName = 'ActionsCellRenderer';

export default ActionsCellRenderer;
